import { useState, useCallback } from "react";

/**
 * useSegmentSelection – Manages segment selection state for image and video generation.
 *
 * @param {Array} segments – The current list of montage segments.
 * @returns {object} Selection state and toggle handlers.
 */
export default function useSegmentSelection(segments) {
    // Image generation selection
    const [selectedSegments, setSelectedSegments] = useState([]);
    // Video generation selection
    const [selectedVideoSegments, setSelectedVideoSegments] = useState([]);

    // Toggle a single segment for image generation
    const toggleSegmentSelection = useCallback((segmentId) => {
        setSelectedSegments(prev =>
            prev.includes(segmentId)
                ? prev.filter(id => id !== segmentId)
                : [...prev, segmentId]
        );
    }, []);

    // Select / deselect all segments for image generation
    const toggleAllSegments = useCallback(() => {
        if (selectedSegments.length === segments.length) {
            setSelectedSegments([]);
        } else {
            setSelectedSegments(segments.map(s => s.segment_id));
        }
    }, [selectedSegments.length, segments]);

    // Toggle a single segment for video generation
    const toggleVideoSegmentSelection = useCallback((segmentId) => {
        setSelectedVideoSegments(prev =>
            prev.includes(segmentId)
                ? prev.filter(id => id !== segmentId)
                : [...prev, segmentId]
        );
    }, []);

    // Select / deselect all segments for video generation
    const toggleAllVideoSegments = useCallback(() => {
        if (selectedVideoSegments.length === segments.length) {
            setSelectedVideoSegments([]);
        } else {
            setSelectedVideoSegments(segments.map(s => s.segment_id));
        }
    }, [selectedVideoSegments.length, segments]);

    return {
        selectedSegments,
        setSelectedSegments,
        selectedVideoSegments,
        setSelectedVideoSegments,
        toggleSegmentSelection,
        toggleAllSegments,
        toggleVideoSegmentSelection,
        toggleAllVideoSegments,
    };
}
